/** Tablet-only show/hide for the second rail (section or context navigation beside the main rail).
 *  A landscape tablet has little width to spare, so the user can put the rail away from a header
 *  button and get it back the same way. The choice persists per browser. The header and the
 *  sidebar talk over `section-rail:toggle` / `section-rail:state-change`. */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

export const TABLET_SECOND_RAIL_HIDDEN_KEY = 'quantis:tablet-second-rail-hidden:v1';

export interface SectionRailState {
  /** A second rail exists on this page and may be put away. */
  available: boolean;
  hidden: boolean;
}

function readStored(): boolean {
  try {
    return window.localStorage.getItem(TABLET_SECOND_RAIL_HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Sidebar side: owns the stored choice and answers the header. */
export function useTabletSecondRail(available: boolean) {
  const [stored, setStored] = useState(readStored);
  const hidden = available && stored;

  const toggle = useCallback(() => {
    setStored((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(TABLET_SECOND_RAIL_HIDDEN_KEY, next ? '1' : '0');
      } catch {
        // Private mode: the rail still hides for this session.
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    const publish = () => window.dispatchEvent(new CustomEvent<SectionRailState>('section-rail:state-change', {
      detail: { available, hidden },
    }));
    window.addEventListener('section-rail:request-state', publish);
    publish();
    return () => window.removeEventListener('section-rail:request-state', publish);
  }, [available, hidden]);

  useEffect(() => {
    if (!available) return;
    window.addEventListener('section-rail:toggle', toggle);
    return () => window.removeEventListener('section-rail:toggle', toggle);
  }, [available, toggle]);

  return hidden;
}

/** Header side: mirrors the sidebar's answer. */
export function useSectionRailHeaderState(): SectionRailState {
  const [state, setState] = useState<SectionRailState>({ available: false, hidden: false });
  useEffect(() => {
    const onState = (event: Event) => setState((event as CustomEvent<SectionRailState>).detail);
    window.addEventListener('section-rail:state-change', onState);
    window.dispatchEvent(new CustomEvent('section-rail:request-state'));
    return () => window.removeEventListener('section-rail:state-change', onState);
  }, []);
  return state;
}
